import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Plus } from "lucide-react";

export default function PortalReclamosPage() {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo:"recoleccion", asunto:"", descripcion:"", direccion:"" });

  const load = () => axios.get(`${API}/reclamos`, auth).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.asunto || !form.descripcion) { toast.error("Indique asunto y descripción"); return; }
    try {
      const r = await axios.post(`${API}/reclamos`, form, auth);
      toast.success(`Reclamo registrado · Ticket ${r.data.nro_ticket}`);
      setOpen(false); setForm({ tipo:"recoleccion", asunto:"", descripcion:"", direccion:"" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const badge = (e) => {
    const meta = {
      abierto:     { label: "Abierto",     color: "bg-orange-100 text-orange-800" },
      en_revision: { label: "En revisión", color: "bg-blue-100 text-blue-800" },
      en_proceso:  { label: "En proceso",  color: "bg-purple-100 text-purple-800" },
      resuelto:    { label: "Resuelto",    color: "bg-emerald-100 text-emerald-800" },
      cerrado:     { label: "Cerrado",     color: "bg-slate-200 text-slate-700" },
      rechazado:   { label: "Rechazado",   color: "bg-rose-100 text-rose-800" },
      reabierto:   { label: "Reabierto",   color: "bg-amber-100 text-amber-800" },
    }[e || "abierto"] || { label: e, color: "bg-slate-100" };
    return <Badge className={`${meta.color} hover:${meta.color}`}>{meta.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="portal-reclamos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-orange-500" />Mis Reclamos
          </h1>
          <p className="text-green-700/70 mt-1">Reporte fallas en la recolección de basura o consultas</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-reclamo" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nuevo reclamo</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo reclamo</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo:v})}>
                  <SelectTrigger data-testid="reclamo-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recoleccion">Fallas en recolección</SelectItem>
                    <SelectItem value="factura">Problemas con factura</SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Asunto* <span className="text-xs text-slate-400">(3-200 caracteres)</span></Label><Input data-testid="reclamo-asunto" value={form.asunto} maxLength={200} minLength={3} required onChange={e => setForm({...form, asunto:e.target.value})} /></div>
              <div><Label>Dirección (si aplica) <span className="text-xs text-slate-400">(máx. 500)</span></Label><Input value={form.direccion} maxLength={500} onChange={e => setForm({...form, direccion:e.target.value})} /></div>
              <div>
                <Label>Descripción detallada* <span className="text-xs text-slate-400">(mín. 5 · máx. 3000)</span></Label>
                <Textarea data-testid="reclamo-descripcion" value={form.descripcion} maxLength={3000} minLength={5} required rows={5} onChange={e => setForm({...form, descripcion:e.target.value})} />
                <p className="text-[10px] text-slate-500 text-right mt-0.5">{(form.descripcion || "").length}/3000</p>
              </div>
            </div>
            <DialogFooter><Button data-testid="btn-submit-reclamo" onClick={submit} className="bg-green-700 hover:bg-green-800 text-white">Enviar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {items.map(r => (
          <Card key={r.id} className="p-5 border-green-100" data-testid={`reclamo-${r.nro_ticket}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-slate-500">{r.nro_ticket}</span>
                  {badge(r.estado)}
                </div>
                <p className="font-display font-bold text-green-900 text-base">{r.asunto}</p>
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{r.descripcion}</p>
                {r.respuesta && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-600">
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wider">Respuesta del municipio</p>
                    <p className="text-sm text-green-900 mt-1 whitespace-pre-wrap">{r.respuesta}</p>
                  </div>
                )}
                {r.motivo_rechazo && (
                  <div className="mt-3 p-3 bg-rose-50 rounded-lg border-l-4 border-rose-600">
                    <p className="text-xs font-bold text-rose-700 uppercase tracking-wider">Motivo de rechazo</p>
                    <p className="text-sm text-rose-900 mt-1 whitespace-pre-wrap">{r.motivo_rechazo}</p>
                  </div>
                )}
                {(r.historial_estados || []).length > 1 && (
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-green-700 font-semibold hover:text-orange-600">
                      Ver historial ({(r.historial_estados || []).length} eventos)
                    </summary>
                    <ol className="mt-2 pl-4 border-l-2 border-green-200 space-y-1.5">
                      {(r.historial_estados || []).slice().reverse().map((h, i) => (
                        <li key={i} className="pl-2">
                          <span className="text-slate-500">{(h.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                          {" · "}<span className="font-semibold capitalize">{(h.estado || "").replace("_", " ")}</span>
                          {h.comentario && <p className="text-slate-600 mt-0.5">{h.comentario}</p>}
                        </li>
                      ))}
                    </ol>
                  </details>
                )}
              </div>
              <span className="text-xs text-slate-400">{(r.created_at||"").slice(0,10)}</span>
            </div>
          </Card>
        ))}
        {!items.length && (
          <Card className="p-8 text-center border-green-100">
            <MessageSquare className="w-12 h-12 text-green-300 mx-auto mb-3" />
            <p className="text-slate-500">Aún no ha reportado reclamos. Use el botón "Nuevo reclamo" para registrar el primero.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
